import { useState, type FormEvent } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_URL = "https://readdy.ai/api/form/db70baecdv04m4p4geb0";

const VALUES = [
  {
    icon: "ri-film-line",
    title: "丰富全面",
    desc: "聚合电影、电视剧等多元影视资源，类型覆盖科幻、悬疑、犯罪、剧情、爱情、古装等，满足不同口味的观影需求。",
  },
  {
    icon: "ri-flashlight-line",
    title: "便捷易用",
    desc: "清晰的分类导航、多维筛选与关键词搜索，帮助用户快速定位目标作品，轻松完成浏览与选择。",
  },
  {
    icon: "ri-refresh-line",
    title: "更新及时",
    desc: "持续追踪最新上映与热播内容，及时更新影视动态，让用户第一时间了解并发现新鲜好片。",
  },
];

const CONTACTS = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "contact@qiuxia-demo.com" },
  { icon: "ri-customer-service-2-line", label: "意见反馈", value: "通过右侧表单提交" },
  { icon: "ri-time-line", label: "服务时间", value: "每日 09:00 - 22:00" },
];

export default function AboutPage() {
  usePageMeta(
    "关于我们 - 秋霞网",
    "了解秋霞网的定位与使命：为用户提供最新最全的电影和电视剧资源与影视动态资讯，打造便捷易用的影视内容门户。"
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [formMessage, setFormMessage] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormMessage("感谢您的反馈，我们会认真阅读每一条建议。");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>),
      });
      const responseText = await res.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const isSuccess = res.ok && parsed?.code === "OK";
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (isSuccess && !isSpam) {
        setStatus("success");
        setFormMessage("感谢您的反馈，我们会认真阅读每一条建议。");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <header className="rounded-xl border border-background-300/60 bg-background-100 px-6 py-8 md:px-10 md:py-12">
        <span className="rounded-full bg-secondary-700 px-3 py-1 text-xs text-primary-400">
          About Qiuxia
        </span>
        <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          关于秋霞网
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-loose text-foreground-500 md:text-base">
          秋霞网是一个以影视内容为核心的在线门户，聚焦为用户提供最新最全的电影和电视剧资源及影视动态资讯。
          我们服务于广泛的影迷群体，包括电影爱好者、追剧用户以及关注影视资讯的用户，致力于让每一位用户都能方便快捷地
          寻找并了解自己感兴趣的影视作品。
        </p>
      </header>

      <section className="mt-10">
        <h2 className="flex items-center gap-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
          <span className="h-6 w-1 rounded-full bg-primary-500" />
          我们的理念
        </h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {VALUES.map((v) => (
            <div
              key={v.title}
              className="rounded-xl border border-background-300/60 bg-background-100 p-6 transition-colors hover:border-primary-400/60"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
                <i className={`${v.icon} text-2xl`} />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                {v.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                {v.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <h2 className="flex items-center gap-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
            <span className="h-6 w-1 rounded-full bg-primary-500" />
            联系方式
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-500">
            如果您对秋霞网有任何建议、资源反馈或合作意向，欢迎通过以下方式与我们联系。
            我们会认真对待每一条反馈，并持续优化网站体验。
          </p>
          <ul className="mt-6 space-y-4">
            {CONTACTS.map((c) => (
              <li
                key={c.label}
                className="flex items-center gap-4 rounded-xl border border-background-300/60 bg-background-100 p-4"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-700 text-primary-400">
                  <i className={`${c.icon} text-xl`} />
                </span>
                <div>
                  <div className="text-xs text-foreground-500">{c.label}</div>
                  <div className="mt-0.5 text-sm text-foreground-900">
                    {c.value}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-6 rounded-xl border border-accent-500/30 bg-accent-500/5 p-5">
            <h3 className="flex items-center gap-2 text-sm font-medium text-foreground-900">
              <i className="ri-error-warning-line text-accent-400" />
              免责声明
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-foreground-500">
              本站为演示站点，所有影视名称、海报、简介及数据均为虚构示例，仅供展示用途。
              本站不提供真实在线播放、下载与存储服务，亦不涉及任何真实影视资源的分发。
            </p>
          </div>
        </div>

        <div>
          <h2 className="flex items-center gap-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
            <span className="h-6 w-1 rounded-full bg-primary-500" />
            意见反馈
          </h2>
          <p className="mt-4 text-sm text-foreground-500">
            填写下方表单告诉我们您的想法，提交后我们会尽快查看。
          </p>

          <form
            id="qiuxia-feedback-form"
            data-readdy-form
            action={FORM_URL}
            method="post"
            onSubmit={handleSubmit}
            className="mt-6 space-y-4 rounded-xl border border-background-300/60 bg-background-100 p-5 md:p-6"
          >
            <div>
              <label
                htmlFor="fb-name"
                className="mb-1.5 block text-sm text-foreground-700"
              >
                您的昵称
              </label>
              <input
                id="fb-name"
                name="name"
                type="text"
                required
                placeholder="请输入昵称"
                className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>

            <div>
              <label
                htmlFor="fb-email"
                className="mb-1.5 block text-sm text-foreground-700"
              >
                联系邮箱
              </label>
              <input
                id="fb-email"
                name="email"
                type="email"
                required
                placeholder="请输入邮箱地址"
                className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>

            <div>
              <label
                htmlFor="fb-subject"
                className="mb-1.5 block text-sm text-foreground-700"
              >
                反馈类型
              </label>
              <select
                id="fb-subject"
                name="subject"
                className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              >
                <option value="资源建议">资源建议</option>
                <option value="功能反馈">功能反馈</option>
                <option value="内容纠错">内容纠错</option>
                <option value="合作洽谈">合作洽谈</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="fb-message"
                className="mb-1.5 block text-sm text-foreground-700"
              >
                反馈内容
              </label>
              <textarea
                id="fb-message"
                name="message"
                required
                maxLength={500}
                rows={4}
                placeholder="请输入您的反馈内容（不超过 500 字）"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>

            <input
              className="form-extra-field"
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={status === "submitting"}
              className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
            >
              {status === "submitting" ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  提交中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line" />
                  提交反馈
                </>
              )}
            </button>

            {status === "success" && (
              <div className="flex items-start gap-2 rounded-md bg-primary-500/10 px-4 py-3 text-sm text-primary-400">
                <i className="ri-checkbox-circle-line mt-0.5" />
                <span>{formMessage}</span>
              </div>
            )}
            {status === "error" && (
              <div className="flex items-start gap-2 rounded-md bg-accent-500/10 px-4 py-3 text-sm text-accent-400">
                <i className="ri-error-warning-line mt-0.5" />
                <span>{formError}</span>
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}